import { format } from "date-fns";
import { useState, useEffect } from "react";
import { useParams } from "react-router";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Checkbox } from "@/components/ui/checkbox";
import MainLayout from "@/components/layouts";
import { getInitials } from "@/lib/utils";
import { useIndividualDetail } from "./individual-detail.hooks";
import { Edit2, FileDown, Mail, Phone, Info, Loader2 } from "lucide-react";
import DocumentUploadModal from "@/components/shared/upload-document/document-upload-modal";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import SimpleTable, { ColumnDef } from "@/components/shared/simple-table";
import type { DocumentRecord } from "@shyne/shared/api";
import { DocumentOpenLink } from "@/components/shared/documents/document-open-link";
import { DocumentDeleteButton } from "@/components/shared/documents/document-delete-button";
import { shownDay } from "@/lib/documents";
import { SkeletonContent } from "@/components/shared/skeleton-content";
import StatusBadge from "@/app/organization/settings/teammembers/teammembers.components/StatusBadge";
import { toUserStatus } from "@/repositories/organization-records";
import { Badge } from "@/components/ui/badge";
import { WorkerAvailability } from "@/components/shared/worker-availability";
import { DailyWorkHourlyRateCard } from "@/components/daily-work/DailyWorkHourlyRateCard";

export default function IndividualDetailPage() {
  const { individualId } = useParams<{ individualId: string }>();
  const {
    detail,
    loading,
    error,
    documents,
    documentsTotal,
    documentsPage,
    documentsPageSize,
    uploadModalOpen,
    uploadingDocument,
    updatingDocumentId,
    savingServices,
    serviceError,
    toggleService,
    uploadDocument,
    deleteDocument,
    updateDocumentExpiresAt,
    setUploadDocumentModalOpen,
    setDocPage,
    setDocPageSize,
  } = useIndividualDetail(individualId);

  const servicesList = detail?.contract.services ?? [];
  const [deletingDocId, setDeletingDocId] = useState<string | null>(null);
  const [editingDocument, setEditingDocument] = useState<DocumentRecord | null>(null);
  const [expiresAtValue, setExpiresAtValue] = useState("");

  const selectedServices = detail?.serviceIds ?? [];

  const handleToggleService = async (serviceId: string) => {
    const isCurrentlySelected = selectedServices.includes(serviceId);
    await toggleService(serviceId, !isCurrentlySelected);
  };

  const handleDocumentUpload = async (file: File, expiresAt?: string | Date) => {
    await uploadDocument(file, expiresAt);
  };

  const handleDeleteDocument = async (docId: string) => {
    setDeletingDocId(docId);
    try {
      await deleteDocument(docId);
    } finally {
      setDeletingDocId(null);
    }
  };

  const openExpiresAtEditor = (document: DocumentRecord) => {
    setEditingDocument(document);
    setExpiresAtValue(document.expiresOn ?? "");
  };

  const closeExpiresAtEditor = () => {
    setEditingDocument(null);
    setExpiresAtValue("");
  };

  const handleSaveExpiresAt = async () => {
    if (!editingDocument) return;
    try {
      await updateDocumentExpiresAt(editingDocument.id, expiresAtValue || null);
      closeExpiresAtEditor();
    } catch {
      // Keep the dialog open; the hook already shows the error toast.
    }
  };

  const documentColumns: ColumnDef<DocumentRecord>[] = [
    {
      header: "Name",
      accessor: (row) => <DocumentOpenLink document={row} />,
    },
    {
      header: "Uploaded",
      accessor: (row) => format(new Date(row.uploadedAt), "MM/dd/yyyy"),
    },
    {
      header: "Expires",
      accessor: (row) => shownDay(row.expiresOn),
    },
    {
      header: "Actions",
      accessor: (row) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="sm" aria-label={`Edit expiration of ${row.filename}`} onClick={() => openExpiresAtEditor(row)} disabled={updatingDocumentId === row.id}>
            {updatingDocumentId === row.id ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Edit2 className="w-4 h-4" />
            )}
          </Button>
          <DocumentDeleteButton document={row} deleting={deletingDocId === row.id} onDelete={handleDeleteDocument} />
        </div>
      ),
      className: "w-28",
    },
  ];

  if (loading) {
    return (
      <MainLayout
        breadcrumb={[
          { name: "Individuals", href: "/individuals" },
          { name: "Loading...", href: "#" },
        ]}
      >
        <SkeletonContent />
      </MainLayout>
    );
  }

  if (error || !detail) {
    return (
      <MainLayout
        breadcrumb={[
          { name: "Individuals", href: "/individuals" },
          { name: "Error", href: "#" },
        ]}
      >
        <Card>
          <CardContent className="text-center py-8">
            <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Info className="h-8 w-8 text-destructive" />
            </div>
            <h3 className="font-semibold text-lg mb-2">Error Loading Individual</h3>
            <p className="text-muted-foreground text-sm">{error || "Individual not found"}</p>
          </CardContent>
        </Card>
      </MainLayout>
    );
  }

  const breadcrumb = [
    { name: "Individuals", href: "/individuals" },
    { name: detail.contract.person.name || "Individual Details", href: "#" },
  ];

  return (
    <MainLayout breadcrumb={breadcrumb}>
      <div className="space-y-6">
        {/* Individual Info Card */}
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <Avatar className="w-16 h-16">
                  <AvatarImage src={detail.contract.person.avatar ?? undefined} />
                  <AvatarFallback>{getInitials(detail.contract.person.name)}</AvatarFallback>
                </Avatar>
                <div>
                  <CardTitle className="text-xl">{detail.contract.person.name}</CardTitle>
                  <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Mail className="w-4 h-4" />
                      {detail.contract.person.email}
                    </div>
                    {detail.contract.person.phone && (
                      <div className="flex items-center gap-1">
                        <Phone className="w-4 h-4" />
                        {detail.contract.person.phone}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={toUserStatus(detail.contract.status)} />
              </div>
            </div>
          </CardHeader>
        </Card>

        <DailyWorkHourlyRateCard
          payer={detail.contract.employer.id}
          payee={{ worker: detail.contract.person.id }}
          services={servicesList.map((service) => ({ id: service.id, name: service.name }))}
          testIdPrefix={`daily-work-rate-individual-${detail.contract.id}`}
        />

        {/* Services and Schedule Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Services Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                Services
                <Button variant="outline" size="sm" onClick={() => setUploadDocumentModalOpen(true)}>
                  <Edit2 className="w-4 h-4 mr-2" />
                  Manage
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {servicesList.map((service) => {
                  const isSelected = selectedServices.includes(service.id);
                  const isSaving = savingServices.includes(service.id);

                  return (
                    <div key={service.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => handleToggleService(service.id)}
                          disabled={isSaving}
                        />
                        <span className="font-medium">{service.name}</span>
                      </div>
                      {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                    </div>
                  );
                })}
                {serviceError && (
                  <div className="text-sm text-destructive bg-destructive/10 p-2 rounded">{serviceError}</div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Schedule Card */}
          <Card>
            <CardHeader>
              <CardTitle>Work Schedule</CardTitle>
            </CardHeader>
            <CardContent>
              <WorkerAvailability contractId={detail.contract.id} workerName={detail.contract.person.name} />
            </CardContent>
          </Card>
        </div>

        {/* Documents Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              Documents
              <Button variant="outline" size="sm" onClick={() => setUploadDocumentModalOpen(true)}>
                <Edit2 className="w-4 h-4 mr-2" />
                Upload Document
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {documents.length > 0 ? (
              <SimpleTable<DocumentRecord>
                columns={documentColumns}
                data={documents}
                totalCount={documentsTotal}
                page={documentsPage}
                pageSize={documentsPageSize}
                setPage={setDocPage}
                setPageSize={setDocPageSize}
                filters={[]}
                onReset={() => {}}
              />
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileDown className="h-8 w-8 text-muted-foreground" />
                </div>
                <h3 className="font-semibold text-lg mb-2">No Documents</h3>
                <p className="text-muted-foreground text-sm mb-4">
                  No documents have been uploaded for this individual yet.
                </p>
                <Button onClick={() => setUploadDocumentModalOpen(true)}>Upload First Document</Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Document Upload Modal */}
      <DocumentUploadModal
        open={uploadModalOpen}
        onOpenChange={setUploadDocumentModalOpen}
        onUpload={handleDocumentUpload}
        uploading={uploadingDocument}
      />
      <Dialog open={!!editingDocument} onOpenChange={(open) => (!open ? closeExpiresAtEditor() : undefined)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Expiration Date</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="individual-document-expires-at">Expires At</Label>
            <Input
              id="individual-document-expires-at"
              type="date"
              value={expiresAtValue}
              onChange={(event) => setExpiresAtValue(event.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeExpiresAtEditor}>
              Cancel
            </Button>
            <Button variant="outline" onClick={() => setExpiresAtValue("")}>
              Clear Date
            </Button>
            <Button onClick={handleSaveExpiresAt} disabled={updatingDocumentId === editingDocument?.id}>
              {updatingDocumentId === editingDocument?.id && <Loader2 className="w-4 h-4 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
}
